import { useRef } from "react";
import { Avatar, AvatarImage, AvatarFallback } from "./avatar";
import { Card } from "./card";
import { Button } from "./button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./dialog";

import { useState, useEffect } from "react";
import { PostRepository } from "@/infrastructure/repositories/PostRepository";
import { secureStorage } from "@/utils/secureStorage";

export function PostCard({ post, onDelete, currentUserId, userProfile ,onDownloadPractice }: any) {
  const [showComments, setShowComments] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);
  const [comments, setComments] = useState<any[]>([]);
  const [commentText, setCommentText] = useState("");
  const [commentMedia, setCommentMedia] = useState<File | null>(null);

  const [imgLoaded, setImgLoaded] = useState(false);
  const [avatarLoaded, setAvatarLoaded] = useState(false);

  const [deleteDialog, setDeleteDialog] = useState(false);
  const [uploadingComment, setUploadingComment] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const isMyPost = Number(post.user_id) === Number(currentUserId);

  const postRepo = new PostRepository();
  // ------------------------------------------------------------
  // LOAD COMMENTS FROM API
  // ------------------------------------------------------------
  const loadComments = async () => {
    if (!showComments) return;

    if (comments.length > 0) return;

    try {
      setCommentLoading(true);

      const result = await postRepo.fetchComments(
        post.team_post_id || 0,
        post.id,
        1
      );

      setComments(result || []);
    } finally {
      setCommentLoading(false);
    }
  };

  useEffect(() => {
    loadComments();
  }, [showComments]);

  // ------------------------------------------------------------
  // ADD COMMENT WITH IMAGE/VIDEO SUPPORT
  // ------------------------------------------------------------
  const handleSendComment = async () => {
    if (!commentText.trim() && !commentMedia) return;

    setUploadingComment(true);

    let uploadedPhoto = "";
    let uploadedVideo = "";

    try {

      // IMAGE
      if (commentMedia && commentMedia.type.startsWith("image/")) {
        const up = await postRepo.uploadCommentImage(commentMedia, false);
        if (up?.status === 1) {
          uploadedPhoto = up.image_link || "";
        }
      }

      // VIDEO
      if (commentMedia && commentMedia.type.startsWith("video/")) {
        const up = await postRepo.uploadCommentVideo(commentMedia);
        if (up?.status === 1) {
          uploadedVideo = up.video_link || "";
        }
      }


      const userId = Number(localStorage.getItem("current_user_swimmer_id")) || 0;
      const coachId = Number(localStorage.getItem("coach_id")) || 0;

      const status = await postRepo.publishComment(
        post.team_post_id || 0,
        post.id,
        userId,
        coachId,
        commentText.trim(),
        uploadedPhoto,
        uploadedVideo
      );

      if (status === 1) {
        // push into local list
        setComments((prev) => [
          ...prev,
          {
            id: Date.now(),
            text: commentText,
            photo: uploadedPhoto,
            video: uploadedVideo,
          },
        ]);
      }
    } finally {
      setUploadingComment(false);
      setCommentMedia(null);
      setCommentText("");

       if (fileInputRef.current) {
    fileInputRef.current.value = "";
  }
    }
  };

  // ------------------------------------------------------------
  // DELETE CONFIRMATION
  // ------------------------------------------------------------
  const confirmDelete = () => {
    setDeleteDialog(true);
  };

  const deletePost = () => {
    setDeleteDialog(false);
    onDelete(post.id);
  };

  // ------------------------------------------------------------
  // MY POST — Replace profile/name
  // ------------------------------------------------------------
  const displayName = isMyPost
    ? secureStorage.get<string>("current_user_name") ?? "Me"
    : (
        `${userProfile?.fname ?? ""} ${userProfile?.lname ?? ""}`.trim() ||
        String(post?.user || "").trim() ||
        "Unknown"
      );

  const displayPic = isMyPost
    ? secureStorage.get("current_user_pic") || "/placeholder-avatar.jpg"
    : userProfile?.image || post?.userPic || "/placeholder-avatar.jpg";

  // ------------------------------------------------------------
return (
  <>
    <Card className="p-4 shadow-sm rounded-xl space-y-4 max-w-xl mx-auto">
      
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">

          <div className="relative h-10 w-10">
            {!avatarLoaded && (
              <div className="absolute inset-0 bg-gray-300 dark:bg-gray-700 animate-pulse rounded-full" />
            )}

            <Avatar className="h-10 w-10">
              <AvatarImage
                src={displayPic}
                loading="lazy"
                className={`${avatarLoaded ? "opacity-100" : "opacity-0"} transition-opacity duration-300`}
                onLoad={() => setAvatarLoaded(true)}
              />
              <AvatarFallback>U</AvatarFallback>
            </Avatar>
          </div>

          <p className="font-semibold text-sm">{displayName}</p>
        </div>

        {isMyPost && (
          <Button
            variant="ghost"
            className="text-red-500 text-xs"
            onClick={confirmDelete}
          >
            Delete
          </Button>
        )}
      </div>

      {/* TEXT */}
      <p className="text-gray-800 text-sm whitespace-pre-wrap break-words">
        {post.text}
      </p>

      {/* MEDIA */}
      {post.video ? (
        <div className="w-full mt-2">
          <video
            src={post.video}
            controls
            preload="metadata"
            className="rounded-lg max-h-72 w-full object-cover"
          />
        </div>
      ) : post.photo ? (
        <div className="w-full mt-2 relative">
          {!imgLoaded && (
            <div className="w-full h-64 bg-gray-300 animate-pulse rounded-lg" />
          )}

          <img
            src={post.photo}
            loading="lazy"
            className={`rounded-lg max-h-72 w-full object-cover transition-opacity duration-500 ${
              imgLoaded ? "opacity-100" : "opacity-0"
            }`}
            onLoad={() => setImgLoaded(true)}
          />
        </div>
      ) : null}

      {/* DOWNLOAD PRACTICE BUTTON */}
      {Number(post.practice_id) > 0 && (
        <Button
          variant="outline"
          className="w-full text-sm mt-2"
          onClick={() => onDownloadPractice(Number(post.practice_id))}
        >
          Download Practice PDF
        </Button>
      )}

      {/* COMMENT BUTTON */}
      <Button
        variant="outline"
        className="w-full text-sm"
        onClick={() => setShowComments((v) => !v)}
      >
        {showComments ? "Hide Comments" : "View Comments"}
      </Button>

      {/* COMMENTS */}
      {showComments && (
        <div className="space-y-3 bg-gray-50 p-3 rounded-lg">
          {commentLoading && (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="h-4 w-full bg-gray-300 animate-pulse rounded-md"
                />
              ))}
            </div>
          )}

          {!commentLoading &&
            comments.map((c: any) => (
              <div
                key={c.id}
                className="p-2 bg-white border text-xs rounded-md space-y-1 break-words max-w-full"
              >
                <p className="break-words whitespace-pre-wrap">{c.text}</p>

                {c.photo && (
                  <img src={c.photo} className="rounded max-h-32 w-full object-contain" />
                )}

                {c.video && (
                  <video src={c.video} controls className="rounded max-h-40 w-full object-contain" />
                )}
              </div>
            ))}

          {/* ADD COMMENT */}
          <div className="flex flex-col space-y-2">
            <input
              className="border rounded-lg p-2 text-sm"
              placeholder="Write a comment..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
            />

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setCommentMedia(file);
              }}
            />

            <Button
              className="text-sm"
              disabled={uploadingComment}
              onClick={handleSendComment}
            >
              {uploadingComment ? "Sending..." : "Send"}
            </Button>
          </div>
        </div>
      )}

    </Card>

    {/* DELETE DIALOG */}
    <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
      <DialogContent className="max-w-xs">
        <DialogHeader>
          <DialogTitle>Delete Post?</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-gray-600">
          Are you sure you want to delete this post?
        </p>

        <div className="flex justify-end space-x-2 mt-4">
          <Button variant="outline" onClick={() => setDeleteDialog(false)}>
            Cancel
          </Button>

          <Button variant="destructive" onClick={deletePost}>
            Delete
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  </>
);


}
